import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import DownloadSharpIcon from "@mui/icons-material/DownloadSharp";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { styled as muiStyled } from "@mui/material/styles";
import type { DigitalSample } from "hooks/digital-samples";
import { type Dispatch, type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { logCustomEvent } from "../../datadog";
import { DigitalSampleDownloadDialog } from "./DigitalSampleDownloadDialog";

const StyledList = muiStyled("div")`
  margin-top: 10px;
  border: 1px solid #D9D9D9;
  display: inline-block;
  padding: 3px 8px 3px 8px;
  border-radius: 2px;

  .bullet {
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: #00bb7e;
    border-radius: 50%;
    margin-right: 5px;
  }
`;

export interface DigitalSampleCardProps {
  value: DigitalSample;
  material: ColoredMaterial;
  onZoom: Dispatch<void>;
  isInternal: boolean | undefined;
}

export const DigitalSampleCard: FC<DigitalSampleCardProps> = ({
  value,
  onZoom,
  material,
  isInternal,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { t } = useTranslation();

  const squarePreview = (value.assets ?? [])
    .filter((a) => a.tags.includes("SMALL"))
    .filter((a) => a.tags.includes("SQUARE"))?.[0];

  return (
    <>
      <Card
        variant="outlined"
        sx={{ display: "flex", justifyContent: "stretch" }}
      >
        <CardActionArea
          onClick={() => {
            logCustomEvent("digital-sample-view", {
              action: "zoom",
              digital_sample_id: value.id,
            });
            onZoom();
          }}
          sx={{ width: "auto", zIndex: 0 }}
        >
          {squarePreview && (
            <CardMedia
              component="img"
              image={squarePreview?.assetUrl}
              alt={squarePreview?.fileName}
              sx={{
                height: "120px",
                width: "120px",
                borderRight: `1px solid ${theme.palette.divider}`,
              }}
            />
          )}
        </CardActionArea>
        <Box
          display="flex"
          flexGrow={1}
          flexDirection="row"
          justifyContent="space-between"
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography component="div" variant="overline">
              Digital Sample
            </Typography>
            <Stack direction={"row"} gap={1}>
              <Typography variant="label">Finish</Typography>
              <span>{value.materialFinish}</span>
            </Stack>
            {value?.isPublished && isInternal ? (
              <StyledList>
                <span className="bullet" />
                <span>{t("available_for_external_users")} </span>
              </StyledList>
            ) : (
              ""
            )}
          </CardContent>
          <CardActions>
            <Button
              endIcon={<DownloadSharpIcon />}
              title="Downloads"
              onClick={handleOpen}
              aria-label="Downloads"
            >
              Downloads
            </Button>
          </CardActions>
        </Box>
      </Card>
      <DigitalSampleDownloadDialog
        open={open}
        onClose={handleClose}
        digitalSample={value}
        material={material}
      />
    </>
  );
};
